import { Loader } from '@myosh/odin-components';
import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { apiBaseUrl, apiKey } from '../env';
import { getClusterConfig } from '../helpers/get-cluster-config';
import { useUser } from './user.context';

const fallbackClusterConfig: ClusterConfig = {
  apiBaseUrl,
  apiKey,
};

/** Read the mobile-injected ClusterConfig from localStorage, falling back to env exports. */
const readInjectedConfigOrFallback = (): ClusterConfig => {
  try {
    const stored = localStorage.getItem('ClusterConfig');
    if (stored) {
      const parsed = JSON.parse(stored) as ClusterConfig;
      if (parsed.apiBaseUrl && parsed.apiKey) return parsed;
    }
  } catch {
    // malformed JSON — fall through
  }
  return fallbackClusterConfig;
};

interface ClusterConfig {
  apiBaseUrl: string;
  apiKey: string;
}

interface ClusterContextProps {
  children: React.ReactNode;
}

const ClusterContext = createContext<ClusterConfig | undefined>(undefined);

export const ClusterProvider: React.FC<ClusterContextProps> = ({ children }) => {
  const [clusterConfigState, setClusterConfigState] = useState<ClusterConfig>();
  const { state: userState } = useUser();
  const schema = userState?.user?.currentSchema;
  const idToken = userState?.tokens?.id_token;

  useEffect(() => {
    const fetchConfig = () => {
      getClusterConfig(idToken ?? '', schema ?? '')
        .then((response) => {
          const { 'api-base-url': clusterApiBaseUrl, 'api-key': clusterApiKey } = response;
          const clusterConfig =
            clusterApiBaseUrl && clusterApiKey
              ? { apiBaseUrl: clusterApiBaseUrl, apiKey: clusterApiKey }
              : fallbackClusterConfig;
          // Only write when we have real values — in the mobile WebView build
          // the fallback can be empty strings and localStorage already has the
          // mobile-injected ClusterConfig that must not be overwritten.
          if (clusterConfig.apiBaseUrl && clusterConfig.apiKey) {
            localStorage.setItem('ClusterConfig', JSON.stringify(clusterConfig));
            setClusterConfigState(clusterConfig);
          } else {
            setClusterConfigState(readInjectedConfigOrFallback());
          }
        })
        .catch(() => {
          if (fallbackClusterConfig.apiBaseUrl && fallbackClusterConfig.apiKey) {
            localStorage.setItem('ClusterConfig', JSON.stringify(fallbackClusterConfig));
            setClusterConfigState(fallbackClusterConfig);
          } else {
            setClusterConfigState(readInjectedConfigOrFallback());
          }
        });
    };

    fetchConfig();
  }, []);

  return (
    <ClusterContext.Provider value={clusterConfigState}>
      {clusterConfigState ? (
        children
      ) : (
        <div className="mx-auto flex h-screen w-full flex-col items-center justify-center">
          <Loader title="Loading configuration" />
        </div>
      )}
    </ClusterContext.Provider>
  );
};

// context consumer hook
export const useCluster = () => {
  const context = useContext(ClusterContext);

  if (context === undefined) {
    throw new Error('useCluster must be used within ClusterProvider');
  }

  return context;
};
